'use client';

import React from 'react';
import clsx from 'clsx';
import { JContainer } from '@/app/components/ui/layout';
import TopContainer from '@/app/components/business/blog/top-container';
import { useParams } from 'next/navigation';
import { getStaticAssetUrl } from '@/app/lib/utils/static-asset-url';

// Loading animation inspired by common skeletons
const shimmer =
    'before:absolute before:inset-0 before:-translate-x-full before:animate-[shimmer_2s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/60 before:to-transparent';

export function ArticleCardSkeleton({
    layout = 'vertical',
}: {
    layout?: 'vertical' | 'horizontal';
}) {
    return (
        <div
            className={clsx(
                'tablet:rounded-[12px] desktop:rounded-[16px] grid h-full w-full overflow-hidden rounded-[8px] border border-[#E8E8E8]',
                {
                    'grid-cols-1': layout === 'vertical',
                    'grid-cols-2': layout === 'horizontal',
                },
            )}
        >
            {/* Image Skeleton */}
            <div
                className={clsx(
                    'relative flex w-full overflow-hidden bg-gray-100',
                    `${shimmer}`,
                    {
                        'aspect-[82/37]': layout === 'vertical', // matches fill-width roughly
                        'h-full min-h-[200px]': layout === 'horizontal',
                    },
                )}
            ></div>

            {/* Content Skeleton */}
            <div className="tablet:p-[20px] desktop:p-[24px] flex h-full w-full flex-col gap-[12px] bg-white p-[16px]">
                <div
                    className={`h-6 w-3/4 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                />
                <div
                    className={`h-6 w-1/2 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                />

                <div className="flex-grow" />

                <div className="mt-4 flex w-full items-end justify-between">
                    <div
                        className={`h-4 w-1/3 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-4 w-1/4 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                </div>
            </div>
        </div>
    );
}

interface BlogListSkeletonProps {
    type?: 'news' | 'research' | 'weekly' | 'default';
}

export function BlogListSkeleton({ type = 'default' }: BlogListSkeletonProps) {
    const params = useParams() as { locale?: string };
    const locale = params?.locale || 'en';

    const skeletonTitles = {
        en: {
            news: 'News',
            research: 'Research',
            weekly: 'Weekly',
        },
        zh: {
            news: '新闻',
            research: '研究',
            weekly: '周刊',
        },
    } as const;
    const localizedTitles = skeletonTitles[locale === 'zh' ? 'zh' : 'en'];

    let topProps = {
        backgroundColor: '#FFFFFF',
        backgroundImage: getStaticAssetUrl('/svgs/blog_top_bg.svg'),
        title: '',
        titleIcon: '',
        mainImage: {
            default: getStaticAssetUrl(
                locale === 'zh'
                    ? '/blog/joinai_blog_top_guide_zh.webp'
                    : '/blog/joinai_blog_top_guide.webp',
            ),
            small: getStaticAssetUrl(
                locale === 'zh'
                    ? '/blog/joinai_blog_top_guide_small_zh.webp'
                    : '/blog/joinai_blog_top_guide_small.webp',
            ),
        },
    };

    if (type === 'news') {
        topProps = {
            backgroundColor: '#4CC773',
            backgroundImage: getStaticAssetUrl('/svgs/news_top_bg.svg'),
            title: localizedTitles.news,
            titleIcon: 'news-black',
            mainImage: {
                default: getStaticAssetUrl(
                    locale === 'zh'
                        ? '/blog/joinai_blog_news_top_guide_zh.webp'
                        : '/blog/joinai_blog_news_top_guide.webp',
                ),
                small: getStaticAssetUrl(
                    locale === 'zh'
                        ? '/blog/joinai_blog_news_top_guide_small_zh.webp'
                        : '/blog/joinai_blog_news_top_guide_small.webp',
                ),
            },
        };
    } else if (type === 'research') {
        topProps = {
            backgroundColor: '#56B9FF',
            backgroundImage: getStaticAssetUrl('/svgs/research_top_bg.svg'),
            title: localizedTitles.research,
            titleIcon: 'research-black',
            mainImage: {
                default: getStaticAssetUrl(
                    locale === 'zh'
                        ? '/blog/joinai_blog_research_top_guide_zh.webp'
                        : '/blog/joinai_blog_research_top_guide.webp',
                ),
                small: getStaticAssetUrl(
                    locale === 'zh'
                        ? '/blog/joinai_blog_research_top_guide_small_zh.webp'
                        : '/blog/joinai_blog_research_top_guide_small.webp',
                ),
            },
        };
    } else if (type === 'weekly') {
        topProps = {
            backgroundColor: '#FFBE6A',
            backgroundImage: getStaticAssetUrl('/svgs/weekly_top_bg.svg'),
            title: localizedTitles.weekly,
            titleIcon: 'weekly-black',
            mainImage: {
                default: getStaticAssetUrl(
                    locale === 'zh'
                        ? '/blog/joinai_blog_weekly_top_guide_zh.webp'
                        : '/blog/joinai_blog_weekly_top_guide.webp',
                ),
                small: getStaticAssetUrl(
                    locale === 'zh'
                        ? '/blog/joinai_blog_weekly_top_guide_small_zh.webp'
                        : '/blog/joinai_blog_weekly_top_guide_small.webp',
                ),
            },
        };
    }

    return (
        <div className="mt-[var(--menu-height)] flex min-h-screen w-full flex-col items-center bg-white">
            {/* Optional Wrapper TopContainer to Match Exactly */}
            {['news', 'research', 'weekly', 'default'].includes(type) ? (
                <TopContainer
                    backgroundColor={topProps.backgroundColor}
                    backgroundImage={topProps.backgroundImage}
                    title={topProps.title}
                    titleIcon={topProps.titleIcon}
                    mainImage={topProps.mainImage}
                >
                    {type === 'default' ? (
                        <div className="tablet:mt-[60px] desktop:mt-[80px] z-10 mt-[40px] flex w-full justify-center">
                            <div className="flex w-full justify-center">
                                <div
                                    className={clsx(
                                        'flex h-full w-full max-w-[1440px] items-center justify-center',
                                        'desktop:flex-row flex-col',
                                        'tablet:gap-[20px] desktop:gap-[24px] gap-[24px]',
                                        'm-mobile:w-[480px] tablet:w-[87.5vw] w-[100vw]',
                                    )}
                                >
                                    <div className="desktop:w-[calc(58%-24px*0.42)] flex w-full">
                                        <ArticleCardSkeleton layout="vertical" />
                                    </div>
                                    <div
                                        className={clsx(
                                            'desktop:max-w-full tablet:max-w-full grid h-full max-w-[344px]',
                                            'desktop:w-[calc(42%-24px*0.58)] tablet:w-full mobile:w-[87.5vw] m-mobile:w-[87.5vw] w-[87.5vw]',
                                            'tablet:grid-cols-2 desktop:grid-cols-1 grid-cols-1',
                                            'tablet:gap-[20px] desktop:gap-[24px] gap-[24px]',
                                        )}
                                    >
                                        <div className="flex w-full flex-col">
                                            <div className="desktop:block hidden h-full w-full">
                                                <ArticleCardSkeleton layout="horizontal" />
                                            </div>
                                            <div className="desktop:hidden block h-full w-full">
                                                <ArticleCardSkeleton layout="vertical" />
                                            </div>
                                        </div>
                                        <div className="flex w-full flex-col">
                                            <div className="desktop:block hidden h-full w-full">
                                                <ArticleCardSkeleton layout="horizontal" />
                                            </div>
                                            <div className="desktop:hidden block h-full w-full">
                                                <ArticleCardSkeleton layout="vertical" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <JContainer
                            size="md"
                            padding="none"
                            className="desktop:flex-row tablet:rounded-[12px] desktop:rounded-[16px] tablet:w-[87vw] desktop:w-[87vw] tablet:max-w-full desktop:max-w-[1440px] flex w-[90vw] max-w-[480px] flex-col overflow-hidden rounded-[8px] border border-gray-100 bg-white"
                        >
                            <div
                                className={`desktop:w-[65%] relative aspect-[936/540] w-full bg-gray-200 ${shimmer} overflow-hidden`}
                            />
                            <div className="desktop:w-[35%] tablet:p-[24px] desktop:p-[32px] flex w-full flex-col justify-center gap-[16px] p-[16px]">
                                <div
                                    className={`h-8 w-4/5 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                                />
                                <div
                                    className={`h-8 w-3/5 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                                />
                                <div className="mt-4 flex flex-col gap-2">
                                    <div
                                        className={`h-4 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                                    />
                                    <div
                                        className={`h-4 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                                    />
                                    <div
                                        className={`h-4 w-2/3 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                                    />
                                </div>
                            </div>
                        </JContainer>
                    )}
                </TopContainer>
            ) : (
                <div
                    className="tablet:py-20 desktop:py-24 relative flex w-full flex-col items-center justify-center py-12"
                    style={{ backgroundColor: topProps.backgroundColor }}
                >
                    <JContainer
                        size="md"
                        padding="none"
                        className="desktop:flex-row tablet:rounded-[12px] desktop:rounded-[16px] tablet:w-[87vw] desktop:w-[87vw] tablet:max-w-full desktop:max-w-[1440px] flex w-[90vw] max-w-[480px] flex-col overflow-hidden rounded-[8px] border border-gray-100 bg-white"
                    >
                        <div
                            className={`desktop:w-[65%] relative aspect-[936/540] w-full bg-gray-200 ${shimmer} overflow-hidden`}
                        />
                        <div className="desktop:w-[35%] tablet:p-[24px] desktop:p-[32px] flex w-full flex-col justify-center gap-[16px] p-[16px]">
                            <div
                                className={`h-8 w-4/5 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                            />
                            <div
                                className={`h-8 w-3/5 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                            />
                            <div className="mt-4 flex flex-col gap-2">
                                <div
                                    className={`h-4 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                                />
                                <div
                                    className={`h-4 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                                />
                                <div
                                    className={`h-4 w-2/3 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                                />
                            </div>
                        </div>
                    </JContainer>
                </div>
            )}

            {/* Grid Skeleton */}
            <section className="tablet:max-w-full desktop:max-w-[1160px] tablet:pt-[80px] desktop:pt-[100px] flex w-[87.5vw] max-w-[344px] flex-col items-center justify-center bg-white pt-[60px]">
                {/* 过滤器预留位置骨架 */}
                <div className="mb-[22px] flex w-full justify-start">
                    <div
                        className={`h-[40px] w-[300px] rounded-full bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                </div>
                <div className="tablet:gap-[60px] desktop:gap-[80px] mb-[80px] grid w-full grid-cols-1 gap-[24px] md:grid-cols-2 lg:grid-cols-3">
                    <ArticleCardSkeleton />
                    <ArticleCardSkeleton />
                    <ArticleCardSkeleton />
                    <ArticleCardSkeleton />
                    <ArticleCardSkeleton />
                    <ArticleCardSkeleton />
                </div>
            </section>
        </div>
    );
}

export function BlogDetailSkeleton() {
    return (
        <main className="mt-[var(--menu-height)] flex min-h-screen w-full max-w-full flex-col items-center bg-white">
            <article className="tablet:py-20 desktop:py-24 w-full max-w-[800px] px-6 py-12">
                {/* Header Info */}
                <div className="mb-8 flex flex-col items-center justify-center gap-6">
                    <div
                        className={`h-10 w-3/4 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-10 w-2/4 rounded-md bg-gray-200 ${shimmer} relative overflow-hidden`}
                    />

                    <div className="mt-4 flex items-center justify-center gap-4">
                        <div
                            className={`h-10 w-10 rounded-full bg-gray-100 ${shimmer} relative overflow-hidden`}
                        />
                        <div
                            className={`h-6 w-32 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                        />
                    </div>
                </div>

                {/* Hero Image */}
                <div
                    className={`relative mb-12 aspect-video w-full rounded-xl bg-gray-200 ${shimmer} overflow-hidden`}
                />

                {/* Content Lines */}
                <div className="flex flex-col gap-4">
                    <div
                        className={`h-5 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-5 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-5 w-11/12 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-5 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-5 w-4/5 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />

                    <div className="my-6" />

                    <div
                        className={`h-5 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-5 w-10/12 rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                    <div
                        className={`h-5 w-full rounded-md bg-gray-100 ${shimmer} relative overflow-hidden`}
                    />
                </div>
            </article>
        </main>
    );
}
